"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StrategicAlignment() {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: moduleRef,
    isVisible: moduleVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width * 100;
    setSliderPos(Math.max(0, Math.min(100, pct)));
  }, []);
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      updateFromClientX(e.clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current || !e.touches[0]) return;
      updateFromClientX(e.touches[0].clientX);
    };
    const stop = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stop);
    };
  }, [updateFromClientX]);
  const stats = [{
    value: "100%",
    label: "TRANSPARENCY",
    desc: "Clear communication channels ensure you are always informed of project progress."
  }, {
    value: "24/7",
    label: "DEDICATED SUPPORT",
    desc: "Our team remains available to address your evolving business requirements."
  }, {
    value: "360°",
    label: "STRATEGIC VIEW",
    desc: "Comprehensive analysis tailored to drive your long-term organizational success."
  }];
  return <section data-section-id="4611" id="strategic-alignment" className="dark bg-background text-foreground py-24 md:py-40 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-12 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-muted-foreground mb-6 md:mb-8">
            / 05 — ARCHITECTURE OF EXCELLENCE
          </p>
          <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-5xl sm:text-6xl md:text-8xl lg:text-[8rem]">
            <span>STANDARD</span>
            <span className="mx-3 md:mx-6 font-black" style={{
            color: "var(--primary)",
            textShadow: "0 0 30px var(--primary)"
          }}>
              /
            </span>
            <span>ITLS ALIGN.</span>
          </h2>
        </div>

        {}
        <div ref={moduleRef} className={`relative border border-border bg-card/40 backdrop-blur-xl shadow-3xl p-3 md:p-6 transition-all duration-700 ease-out ${moduleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div ref={containerRef} className="relative w-full aspect-[16/9] overflow-hidden select-none cursor-ew-resize" onMouseDown={e => {
          isDraggingRef.current = true;
          updateFromClientX(e.clientX);
        }} onTouchStart={e => {
          isDraggingRef.current = true;
          if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
        }}>
            {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/itls_consulting_focus.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/itls_consulting_focus.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/itls_consulting_focus.png-wvc-sizes" alt="Macro view of professional consulting strategy" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />

            {}
            <div className="absolute inset-0 overflow-hidden" style={{
            clipPath: `inset(0 ${100 - sliderPos}% 0 0)`
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/standard_business_process.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/standard_business_process.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/standard_business_process.png-wvc-sizes" alt="Macro view of standard business process" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            </div>

            {}
            <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10 pointer-events-none">
              <div className="bg-background/60 backdrop-blur-md border border-border px-3 py-1.5 md:px-4 md:py-2 shadow-lg">
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-foreground">
                  STANDARD · BASE
                </span>
              </div>
            </div>

            {}
            <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10 pointer-events-none">
              <div className="backdrop-blur-md border px-3 py-1.5 md:px-4 md:py-2 shadow-lg" style={{
              borderColor: "var(--primary)",
              backgroundColor: "color-mix(in oklab, var(--primary) 15%, transparent)"
            }}>
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest" style={{
                color: "var(--primary)"
              }}>
                  ITLS · ALIGNED
                </span>
              </div>
            </div>

            {}
            <div className="absolute top-0 bottom-0 pointer-events-none" style={{
            left: `${sliderPos}%`,
            width: "1px",
            backgroundColor: "var(--primary)",
            boxShadow: "0 0 20px var(--primary), 0 0 40px var(--primary)",
            transform: "translateX(-0.5px)"
          }} />

            {}
            <div className="absolute top-1/2 z-20 flex items-center justify-center" style={{
            left: `${sliderPos}%`,
            transform: "translate(-50%, -50%)"
          }}>
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center backdrop-blur-md border-2 shadow-2xl" style={{
              borderColor: "var(--primary)",
              backgroundColor: "color-mix(in oklab, var(--primary) 20%, var(--background))",
              boxShadow: "0 0 30px var(--primary)"
            }}>
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" style={{
                color: "var(--primary)"
              }} strokeWidth={2.5} />
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" style={{
                color: "var(--primary)"
              }} strokeWidth={2.5} />
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={statsRef} className={`mt-12 md:mt-20 transition-all duration-700 ease-out ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 relative">
            {stats.map((stat, i) => <div key={i} data-index={i} className="relative px-4 md:px-8 py-8 md:py-4 flex flex-col items-start">
                {i > 0 && <Separator orientation="vertical" className="hidden md:block absolute left-0 top-0 bottom-0" />}
                {i > 0 && <Separator orientation="horizontal" className="md:hidden absolute top-0 left-0 right-0" />}
                <div className="font-mono font-bold text-5xl md:text-7xl lg:text-[5rem] leading-none mb-4 md:mb-6 tracking-tight" style={{
              color: "var(--primary)",
              textShadow: "0 0 40px color-mix(in oklab, var(--primary) 50%, transparent)"
            }}>
                  {stat.value}
                </div>
                <p className="font-default font-bold uppercase tracking-widest text-xs md:text-sm text-foreground mb-3">
                  {stat.label}
                </p>
                <p className="font-serif text-base md:text-lg text-muted-foreground leading-relaxed">
                  {stat.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}